// LanguageSelector.jsx
// 번역 언어 선택 팝업 컴포넌트
// - 원문 함께 보기 토글 (강의 언어 isSource 자동 감지)
// - 토글 ON: 번역 언어 목록에서 원문 항목만 제거, 섹션 레이블/강의언어 칩 없음
// - 토글 OFF: 원문 항목 복원
// - 언어 항목 사이 구분선 없음

import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Toggle from '../components/Toggle';
import { ReactComponent as CheckIcon } from '../assets/icons/svg/lecture/check.svg';
import flagMap from '../components/flagMap';

const LanguageSelector = ({
                              show,
                              onClose,
                              availableLanguages,
                              selectedLanguageCode,
                              onSelect,
                              onAutoSelect,
                              getLanguageName,
                              isDarkMode,
                              defaultShowOriginal = false,
                              onShowOriginalChange,
                          }) => {
    const { t } = useTranslation();

    const [showOriginal, setShowOriginal] = useState(defaultShowOriginal);

    // isSource:true인 언어를 강의(원문) 언어로 자동 감지
    const sourceLanguage = useMemo(
        () => availableLanguages.find(l => l.isSource) ?? null,
        [availableLanguages]
    );

    // 토글 ON일 때 번역 언어 목록에서 강의 언어 제외
    const filteredLanguages = useMemo(
        () => showOriginal && sourceLanguage
            ? availableLanguages.filter(lang => lang.code !== sourceLanguage.code)
            : availableLanguages,
        [showOriginal, sourceLanguage, availableLanguages]
    );

    const canShowOriginal = !!sourceLanguage;

    const handleToggleChange = (next) => {
        setShowOriginal(next);
        onShowOriginalChange?.(next);
        // 토글 ON 시 원문이 선택되어 있으면 첫 번째 번역 언어 자동 선택 (모달 닫지 않음)
        if (next && sourceLanguage && selectedLanguageCode === sourceLanguage.code) {
            const firstTranslation = availableLanguages.find(l => l.code !== sourceLanguage.code);
            if (firstTranslation) onAutoSelect?.(firstTranslation.code);
        }
    };

    const toggleDescText = t('languageSelector.toggleLabel', '강의 원문과 함께 보기');

    const panelRef = useRef(null);
    const dragStartY = useRef(null);
    const dragDeltaY = useRef(0);

    useEffect(() => {
        if (show && panelRef.current) {
            panelRef.current.style.transform = '';
            panelRef.current.style.transition = '';
        }
    }, [show]);

    const handleDragStart = useCallback((e) => {
        dragStartY.current = e.touches[0].clientY;
        dragDeltaY.current = 0;
        if (panelRef.current) panelRef.current.style.transition = 'none';
    }, []);

    const handleDragMove = useCallback((e) => {
        if (dragStartY.current === null) return;
        const delta = e.touches[0].clientY - dragStartY.current;
        dragDeltaY.current = Math.max(0, delta);
        if (panelRef.current) panelRef.current.style.transform = `translateY(${dragDeltaY.current}px)`;
    }, []);

    const handleDragEnd = useCallback(() => {
        if (dragStartY.current === null) return;
        if (panelRef.current) panelRef.current.style.transition = '';
        if (dragDeltaY.current > 80) {
            if (panelRef.current) panelRef.current.style.transform = 'translateY(100%)';
            onClose();
        } else if (panelRef.current) {
            panelRef.current.style.transform = '';
        }
        dragStartY.current = null;
        dragDeltaY.current = 0;
    }, [onClose]);

    return (
        <>
            {/* 딤드 백드롭 */}
            <div
                className={`fixed inset-0 z-[25] bg-black transition-opacity duration-300 ${
                    show ? 'bg-opacity-30' : 'bg-opacity-0 pointer-events-none'
                }`}
                onClick={onClose}
            />
            <div
                ref={panelRef}
                className={`fixed left-0 right-0 z-[26] bottom-[calc(env(safe-area-inset-bottom,0px)+4rem)] transition-transform duration-300 ease-out ${
                    show ? 'translate-y-0' : 'translate-y-full'
                } ${isDarkMode ? 'bg-[#1a1f2e]' : 'bg-white'} rounded-t-[20px] min-h-[332px] max-h-[70vh] flex flex-col`}
            >
                {/* 드래그 핸들 */}
                <div
                    className="flex justify-center py-2 cursor-grab touch-none"
                    onTouchStart={handleDragStart}
                    onTouchMove={handleDragMove}
                    onTouchEnd={handleDragEnd}
                >
                    <div className={`w-10 h-1 rounded-[2.5px] ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                </div>

                <div className="px-6 pb-6 flex flex-col flex-1 min-h-0">
                    {/* 헤더 */}
                    <div className="flex items-center justify-between">
                        <h2
                            className={`${isDarkMode ? 'text-white' : 'text-black'} text-sm font-semibold leading-[150%] tracking-[-0.005em]`}
                        >
                            {t('languageSelector.title')}
                        </h2>
                        {/* X 닫기 버튼 - 마우스 환경에서만 표시 */}
                        <button
                            onClick={onClose}
                            className={`pointer-coarse:hidden w-6 h-6 flex items-center justify-center rounded transition-colors duration-200 ${
                                isDarkMode ? 'hover:bg-white/[0.06] active:bg-white/[0.12]' : 'hover:bg-black/[0.03] active:bg-black/[0.06]'
                            }`}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* 원문 함께 보기 토글 */}
                    {canShowOriginal && (
                        <div className="flex items-center justify-between gap-3 mt-[18px]">
                            <span
                            className={`${isDarkMode ? 'text-[#aaa]' : 'text-black'} text-sm font-normal leading-[150%] tracking-[-0.005em]`}
                            >
                                {toggleDescText}
                            </span>
                            <Toggle
                                checked={showOriginal}
                                onChange={handleToggleChange}
                            />
                        </div>
                    )}

                    {canShowOriginal && (
                        <div className={`h-px w-full mt-4 ${isDarkMode ? 'bg-[#333]' : 'bg-[#E0E3E5]'}`} />
                    )}

                    {/* 번역 언어 목록 */}
                    <div className="flex flex-col mt-3.5 gap-[0.7vh] max-h-[calc((44px+0.7vh)*5)] overflow-y-auto scrollbar-hide" style={{ minHeight: `calc((44px + 0.7vh) * ${availableLanguages.length})` }}>
                        {filteredLanguages.map(lang => (
                            <button
                                key={lang.code}
                                onClick={() => onSelect(lang.code)}
                                className={`h-11 rounded-md py-2.5 pr-2 pl-3.5 flex items-center justify-between ${
                                    isDarkMode ? 'text-white' : 'text-black'
                                } ${
                                    selectedLanguageCode === lang.code
                                        ? isDarkMode ? 'bg-[#FFFFFF12]' : 'bg-[#F5F5F5]'
                                        : isDarkMode ? 'hover:bg-[#FFFFFF08]' : 'hover:bg-gray-100'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    {flagMap[lang.code] ? (
                                        <img
                                            src={flagMap[lang.code]}
                                            alt=""
                                            className={`w-6 h-6 rounded-full object-cover border ${isDarkMode ? 'border-[#666]' : 'border-[#EEEFF0]'}`}
                                        />
                                    ) : (
                                        <div className={`w-6 h-6 rounded-full border ${isDarkMode ? 'border-[#666] bg-[#2a2a2a]' : 'border-[#EEEFF0] bg-[#F5F5F5]'}`} />
                                    )}
                                    <span className="text-[15px] leading-[150%] tracking-[-0.005em] font-medium">
                                        {(lang.name || getLanguageName(lang.code)) + (lang.isSource ? ` (${t('languageSelector.originalSuffix')})` : '')}
                                    </span>
                                </div>
                                {selectedLanguageCode === lang.code && (
                                    <CheckIcon className="w-[18px] h-[18px]" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default LanguageSelector;
